import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  MapPin, Star, Shield, Clock, Phone, ChevronLeft, 
  Calendar as CalendarIcon, Send, CreditCard, Check, Package, MessageSquare
} from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgencyDetail = () => {
  const { agencyId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, login } = useAuth();
  
  const [agency, setAgency] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Booking state
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [serviceType, setServiceType] = useState('');
  const [description, setDescription] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [bookingStep, setBookingStep] = useState(1);
  const [bookingLoading, setBookingLoading] = useState(false);
  
  // Inquiry state
  const [showInquiry, setShowInquiry] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [agencyRes, reviewsRes, specsRes] = await Promise.all([
          axios.get(`${API}/agencies/${agencyId}`),
          axios.get(`${API}/reviews/${agencyId}`),
          axios.get(`${API}/specializations`)
        ]);
        
        setAgency(agencyRes.data);
        setReviews(reviewsRes.data);
        setSpecializations(specsRes.data.specializations);
      } catch (error) {
        console.error('Error fetching agency:', error);
        toast.error('Agency not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [agencyId, navigate]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleBooking = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to book');
      login('user');
      return;
    }

    if (!selectedDate || !serviceType || !description) {
      toast.error('Please fill all required fields');
      return;
    }

    // Check if plans exist but none selected
    if (agency?.plans?.length > 0 && !selectedPlan) {
      toast.error('Please select a plan');
      return;
    }

    setBookingLoading(true);
    try {
      const bookingData = {
        agency_id: agencyId,
        date: format(selectedDate, 'yyyy-MM-dd'),
        service_type: serviceType,
        description,
        contact_phone: contactPhone
      };

      // Add plan info if selected
      if (selectedPlan) {
        bookingData.plan_id = selectedPlan.plan_id;
        bookingData.plan_name = selectedPlan.name;
        bookingData.amount = selectedPlan.price;
      }

      const response = await axios.post(`${API}/bookings`, bookingData, { withCredentials: true });

      // Proceed to payment
      const paymentResponse = await axios.post(`${API}/payments/checkout`, {
        booking_id: response.data.booking_id,
        origin_url: window.location.origin
      }, { withCredentials: true });

      // Redirect to Stripe
      window.location.href = paymentResponse.data.url;
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleInquiry = async () => {
    if (!inquiryForm.name || !inquiryForm.email || !inquiryForm.phone || !inquiryForm.message) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await axios.post(`${API}/inquiries`, {
        agency_id: agencyId,
        ...inquiryForm
      }, { withCredentials: true });

      toast.success('Inquiry sent successfully!');
      setShowInquiry(false);
      setInquiryForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Inquiry error:', error);
      toast.error('Failed to send inquiry');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!agency) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="sticky top-0 bg-white border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="font-display text-xl font-bold text-slate-900">My Detective</span>
            </button>
            
            {isAuthenticated ? (
              <div className="h-8 w-8 rounded-full bg-slate-200 overflow-hidden">
                {user?.picture && <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />}
              </div>
            ) : (
              <Button 
                onClick={() => login('user')}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-none"
                data-testid="login-btn"
              >
                Login
              </Button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Agency Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <div className="bg-white border border-slate-200 p-6" data-testid="agency-header">
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="w-24 h-24 bg-slate-100 border border-slate-200 flex-shrink-0 overflow-hidden">
                  {agency.image_url ? (
                    <img src={agency.image_url} alt={agency.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                      <Shield className="w-8 h-8" />
                    </div>
                  )}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" data-testid="agency-name">
                        {agency.name}
                      </h1>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex items-center gap-1 text-slate-500">
                          <MapPin className="w-4 h-4" />
                          {agency.city}
                        </div>
                        {agency.verified && (
                          <div className="flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 border border-emerald-200">
                            <Shield className="w-3 h-3" />
                            Verified
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="flex items-center gap-1">
                        <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                        <span className="font-display text-xl font-bold text-slate-900">{agency.rating}</span>
                      </div>
                      <p className="text-sm text-slate-500">{agency.total_reviews} reviews</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 mt-4 text-sm">
                    <div className="flex items-center gap-1 text-slate-600">
                      <Clock className="w-4 h-4" />
                      {agency.years_experience} years experience
                    </div>
                    {agency.phone && (
                      <div className="flex items-center gap-1 text-slate-600">
                        <Phone className="w-4 h-4" />
                        {agency.phone}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            {agency.description && (
              <div className="bg-white border border-slate-200 p-6">
                <h2 className="font-display text-lg font-bold text-slate-900 mb-3">About</h2>
                <p className="text-slate-600 leading-relaxed">{agency.description}</p>
              </div>
            )}

            {/* Specializations */}
            <div className="bg-white border border-slate-200 p-6">
              <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Specializations</h2>
              <div className="flex flex-wrap gap-2">
                {agency.specializations?.map((spec, idx) => (
                  <span 
                    key={idx}
                    className="text-sm text-slate-700 bg-slate-100 px-3 py-1.5 border border-slate-200"
                  >
                    {spec}
                  </span>
                ))}
              </div>
            </div>

            {/* Reviews */}
            <div className="bg-white border border-slate-200 p-6">
              <h2 className="font-display text-lg font-bold text-slate-900 mb-4">
                Reviews ({reviews.length})
              </h2>
              
              {reviews.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No reviews yet</p>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.review_id} className="border-b border-slate-200 pb-4 last:border-0">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-slate-900">{review.user_name}</span>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={`star-${review.review_id}-${i}`} 
                              className={`w-4 h-4 ${i < review.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-200'}`} 
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-slate-600">{review.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Booking */}
          <div className="space-y-6">
            {/* Plans Section (if agency has plans) */}
            {agency.plans && agency.plans.length > 0 && (
              <div className="bg-white border border-slate-200 p-6">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-4">
                  Choose a Plan
                </p>
                <div className="space-y-3">
                  {agency.plans.map((plan) => (
                    <div 
                      key={plan.plan_id}
                      onClick={() => setSelectedPlan(plan)}
                      className={`border p-4 cursor-pointer transition-all ${
                        selectedPlan?.plan_id === plan.plan_id
                          ? 'border-slate-900 bg-slate-50'
                          : 'border-slate-200 hover:border-slate-400'
                      }`}
                      data-testid={`plan-${plan.plan_id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-display font-bold text-slate-900">{plan.name}</h4>
                            {selectedPlan?.plan_id === plan.plan_id && (
                              <Check className="w-4 h-4 text-emerald-600" />
                            )}
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{plan.description}</p>
                        </div>
                        <p className="font-display text-xl font-bold text-emerald-600">{formatPrice(plan.price)}</p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                        <Clock className="w-3 h-3" />
                        {plan.duration}
                      </div>
                      {plan.features && plan.features.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {plan.features.slice(0, 4).map((feature) => (
                            <span key={feature} className="text-xs bg-slate-100 px-2 py-0.5">{feature}</span>
                          ))}
                          {plan.features.length > 4 && (
                            <span className="text-xs text-slate-500">+{plan.features.length - 4} more</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Price Card */}
            <div className="bg-white border border-slate-200 p-6 sticky top-24">
              <div className="flex items-baseline justify-between mb-6">
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-1">
                    {selectedPlan ? 'Selected Plan' : 'Starting from'}
                  </p>
                  <p className="font-display text-3xl font-bold text-slate-900">
                    {formatPrice(selectedPlan?.price || agency.base_price)}
                  </p>
                  {selectedPlan && (
                    <p className="text-sm text-slate-600 mt-1">{selectedPlan.name}</p>
                  )}
                </div>
              </div>

              {/* Booking Steps */}
              {bookingStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                      Select Date
                    </label>
                    <div className="border border-slate-200" data-testid="booking-calendar">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={(date) => date < new Date()}
                        className="rounded-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                      Service Type
                    </label>
                    <Select value={serviceType} onValueChange={setServiceType}>
                      <SelectTrigger className="rounded-none border-slate-200" data-testid="service-type-selector">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {specializations.map((spec) => (
                          <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    onClick={() => setBookingStep(2)}
                    disabled={selectedDate === null || serviceType === '' || serviceType === undefined || (agency?.plans?.length > 0 && !selectedPlan)}
                    className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-none h-11 disabled:opacity-50"
                    data-testid="continue-booking-btn"
                  >
                    Continue
                    <CalendarIcon className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {bookingStep === 2 && (
                <div className="space-y-4">
                  <div className="bg-slate-50 border border-slate-200 p-3 text-sm">
                    {selectedPlan && (
                      <p className="text-slate-600 mb-1">
                        <strong>Plan:</strong> {selectedPlan.name} - {formatPrice(selectedPlan.price)}
                      </p>
                    )}
                    <p className="text-slate-600">
                      <strong>Date:</strong> {selectedDate && format(selectedDate, 'PPP')}
                    </p>
                    <p className="text-slate-600">
                      <strong>Service:</strong> {serviceType}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                      Case Description *
                    </label>
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Describe your case requirements..."
                      className="rounded-none border-slate-200 min-h-[100px]"
                      data-testid="case-description"
                    />
                  </div>

                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                      Contact Phone
                    </label>
                    <Input
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      placeholder="+91 XXXXX XXXXX"
                      className="rounded-none border-slate-200"
                      data-testid="contact-phone"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      onClick={() => setBookingStep(1)}
                      variant="outline"
                      className="flex-1 rounded-none border-slate-200"
                    >
                      Back
                    </Button>
                    <Button 
                      onClick={handleBooking}
                      disabled={!description || bookingLoading}
                      className="flex-1 bg-slate-900 text-white hover:bg-slate-800 rounded-none"
                      data-testid="pay-now-btn"
                    >
                      {bookingLoading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          Pay {formatPrice(selectedPlan?.price || agency.base_price)}
                          <CreditCard className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Inquiry Button */}
              <div className="mt-4 pt-4 border-t border-slate-200 space-y-2">
                <Button 
                  onClick={async () => {
                    if (!isAuthenticated) {
                      toast.error('Please login to chat');
                      login('user');
                      return;
                    }
                    try {
                      const response = await axios.post(`${API}/chats`, { agency_id: agencyId }, { withCredentials: true });
                      navigate(`/chat/${response.data.chat_id}`);
                    } catch (error) {
                      console.error('Error creating chat:', error);
                      toast.error('Failed to start chat');
                    }
                  }}
                  variant="outline"
                  className="w-full rounded-none border-slate-200"
                  data-testid="start-chat-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Chat with Agency
                </Button>
                <Button 
                  onClick={() => setShowInquiry(true)}
                  variant="outline"
                  className="w-full rounded-none border-slate-200"
                  data-testid="send-inquiry-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Inquiry Instead
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inquiry Dialog */}
      <Dialog open={showInquiry} onOpenChange={setShowInquiry}>
        <DialogContent className="rounded-none border-slate-200">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold text-slate-900">
              Send Inquiry
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                Your Name *
              </label>
              <Input
                value={inquiryForm.name}
                onChange={(e) => setInquiryForm({...inquiryForm, name: e.target.value})}
                className="rounded-none border-slate-200"
                data-testid="inquiry-name"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                Email *
              </label>
              <Input
                type="email"
                value={inquiryForm.email}
                onChange={(e) => setInquiryForm({...inquiryForm, email: e.target.value})}
                className="rounded-none border-slate-200"
                data-testid="inquiry-email"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                Phone *
              </label>
              <Input
                value={inquiryForm.phone}
                onChange={(e) => setInquiryForm({...inquiryForm, phone: e.target.value})}
                className="rounded-none border-slate-200"
                data-testid="inquiry-phone"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                Message *
              </label>
              <Textarea
                value={inquiryForm.message}
                onChange={(e) => setInquiryForm({...inquiryForm, message: e.target.value})}
                placeholder="Describe your requirements..."
                className="rounded-none border-slate-200 min-h-[100px]"
                data-testid="inquiry-message"
              />
            </div>
            <Button 
              onClick={handleInquiry}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-none"
              data-testid="submit-inquiry-btn"
            >
              Send Inquiry
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgencyDetail;
